import { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import api from '@/api';
import {
  AGENT_TOP_ROUTE_NAME_FMEA,
  AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
  AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL,
  AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
  AGENT_TOP_ROUTE_NAME_PATENT_FTO,
  AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
} from '@/constants';
import { isAxiosError } from 'axios';
import { FunctionKey } from '@/types';
import {
  AccessDecision,
  AvailableFunctionKeysResult,
  evaluateAccess,
  parseAvailableFunctionKeys,
} from '@/utils/contractCapability';
import { getLastUsedAgent } from '@/utils/lastUsedAgent';

const AGENT_TOP_ROUTE_NAMES: Record<FunctionKey, string> = {
  information_retrieval: AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL,
  forced_ideation: AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
  market_sizing: AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
  tech_exploration: AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
  patent_fto: AGENT_TOP_ROUTE_NAME_PATENT_FTO,
  fmea: AGENT_TOP_ROUTE_NAME_FMEA,
};

const CACHE_TTL_MS = 5 * 60 * 1000;

let cachedResult: AvailableFunctionKeysResult | null = null;
let cachedAt = 0;
let inflight: Promise<AvailableFunctionKeysResult> | null = null;

// 401/403は権限情報の取得エラーではなく、ログインし直しが必要なものとして扱い、ログイン画面へ戻す
export class LoginRequiredError extends Error {}

// Vueコンポーネント外（ルーターガード）で使うため、swrvではなくプレーンな関数でキャッシュする
const fetchAvailableFunctionKeysResult =
  async (): Promise<AvailableFunctionKeysResult> => {
    if (cachedResult !== null && Date.now() - cachedAt < CACHE_TTL_MS) {
      return cachedResult;
    }
    if (inflight) return inflight;

    inflight = api
      .currentUser()
      .then(user => {
        const result = parseAvailableFunctionKeys(user.available_function_keys);
        if (result.kind !== 'error') {
          cachedResult = result;
          cachedAt = Date.now();
        }
        return result;
      })
      .catch((error): AvailableFunctionKeysResult => {
        if (
          isAxiosError(error) &&
          (error.response?.status === 401 || error.response?.status === 403)
        ) {
          throw new LoginRequiredError();
        }
        return { kind: 'error' };
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  };

// ログアウト時にも呼ぶ（同一タブで別ユーザーがログインしたときに、前のユーザーの権限情報を使わないため）
export const resetAccessGuardCache = (): void => {
  cachedResult = null;
  cachedAt = 0;
  inflight = null;
};

// 遷移先ではなく拒否理由だけを返す
export const resolveAccessDenialReason = async (
  to: RouteLocationNormalized,
): Promise<Exclude<AccessDecision, 'allow'> | null> => {
  const scope = to.meta.accessScope;
  let decision = evaluateAccess(scope, undefined);
  if (decision === 'pending') {
    decision = evaluateAccess(scope, await fetchAvailableFunctionKeysResult());
  }
  return decision === 'allow' ? null : decision;
};

export const canUseFunction = async (
  functionKey: FunctionKey,
): Promise<boolean> =>
  evaluateAccess(functionKey, await fetchAvailableFunctionKeysResult()) ===
  'allow';

/** 情報収集を利用できるユーザーはホームをそのまま使うため、送り先はない。 */
export const resolveLastUsedAgentLocation = async (
  hasRoute: (routeName: string) => boolean,
): Promise<RouteLocationRaw | null> => {
  const result = await fetchAvailableFunctionKeysResult();
  const canUse = (functionKey: FunctionKey) =>
    evaluateAccess(functionKey, result) === 'allow';

  if (canUse('information_retrieval')) return null;

  const lastUsedAgent = getLastUsedAgent();
  if (lastUsedAgent && canUse(lastUsedAgent)) {
    const routeName = AGENT_TOP_ROUTE_NAMES[lastUsedAgent];
    if (hasRoute(routeName)) return { name: routeName };
  }
  return null;
};

export const resolveFallbackLocation = async (
  hasRoute: (routeName: string) => boolean,
): Promise<RouteLocationRaw> =>
  (await resolveLastUsedAgentLocation(hasRoute)) ?? { path: '/' };
